import { Card, CardHeader, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { useEntitlementsQuery } from '@/hooks/useEntitlements'

function pct(used: number, max: number) {
  if (!max || max <= 0) return 0
  return Math.min(100, Math.round((used / max) * 100))
}

function fmtBytes(n: number) {
  if (Number.isNaN(n)) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let u = 0
  let x = n
  while (x >= 1024 && u < units.length - 1) {
    x /= 1024
    u++
  }
  return `${x.toFixed(1)} ${units[u]}`
}

function fmtDate(value?: string | null) {
  if (!value) return '—'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('fr-FR')
}

export default function EntitlementsCard() {
  const { data, status, isFetching } = useEntitlementsQuery(true)

  if (status === 'pending') {
    return (
      <Card>
        <CardHeader>
          <div className='h-5 w-40 rounded bg-muted animate-pulse' />
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='h-4 w-full rounded bg-muted animate-pulse' />
          <div className='h-4 w-2/3 rounded bg-muted animate-pulse' />
        </CardContent>
      </Card>
    )
  }

  const ok = !!data?.ok

  if (!ok) {
    return (
      <Card>
        <CardHeader>
          <h3 className='text-lg font-semibold'>Quota & droits</h3>
        </CardHeader>
        <CardContent className='text-sm text-muted-foreground'>
          Impossible de charger les droits pour le moment.
        </CardContent>
      </Card>
    )
  }

  const accountActive = !!data?.account?.isActive
  const suspendedAt = data?.account?.suspendedAt ?? null
  const suspensionReason = data?.account?.suspensionReason ?? null

  const planCode = data?.plan?.code ?? '—'
  const nextRenewalAt = data?.plan?.nextRenewalAt ?? null
  const periodKey = data?.messages?.periodKey ?? '—'

  const usedB = Number(data?.storage?.used ?? 0)
  const maxB = Number(data?.storage?.max ?? 0)

  const channels = (['WHATSAPP'] as const).map((ch) => {
    const row = data?.messages?.byChannel?.[ch]

    return {
      channel: ch,
      planIncluded: Number(row?.planIncluded ?? 0),
      topupIncluded: Number(row?.topupIncluded ?? 0),
      totalIncluded: Number(row?.totalIncluded ?? 0),
      used: Number(row?.used ?? 0),
      remaining: Number(row?.remaining ?? 0),
      overageCfa: Number(row?.overageCfa ?? 0),
    }
  })

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between gap-3'>
          <div>
            <h3 className='text-lg font-semibold'>Quota & droits</h3>
            <p className='text-xs text-muted-foreground'>
              Période messages: <b>{periodKey}</b> — Plan: <b>{planCode}</b>
              {nextRenewalAt ? (
                <>
                  {' '}
                  • Renouvellement: <b>{fmtDate(nextRenewalAt)}</b>
                </>
              ) : null}
              {isFetching ? (
                <span className='ml-2 text-[10px] italic'>maj…</span>
              ) : null}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent className='space-y-5'>
        <div>
          <div className='flex items-center justify-between mb-1'>
            <span className='text-sm font-medium'>Stockage</span>
            <span className='text-xs text-muted-foreground'>
              {fmtBytes(usedB)} / {fmtBytes(maxB)}
            </span>
          </div>
          <Progress value={pct(usedB, maxB)} />
        </div>

        <div className='grid gap-4 md:grid-cols-1'>
          {channels.map((v) => (
            <div key={v.channel}>
              <div className='flex items-center justify-between mb-1'>
                <span className='text-sm font-medium'>
                  Messages {v.channel}
                </span>
                <span className='text-xs text-muted-foreground text-right'>
                  {v.used}/{v.totalIncluded}
                  {v.topupIncluded > 0 ? ` • topup ${v.topupIncluded}` : ''}
                  {v.overageCfa > 0 ? ` • ${v.overageCfa} CFA` : ''}
                </span>
              </div>

              <Progress value={pct(v.used, v.totalIncluded)} />

              <div className='mt-1 text-[11px] text-muted-foreground'>
                Plan: {v.planIncluded} • Topup: {v.topupIncluded} • Restant:{' '}
                {v.remaining}
              </div>
            </div>
          ))}
        </div>

        <div className='text-xs text-muted-foreground space-y-1'>
          <div>
            Écriture: {data?.canWrite ? '✅ autorisée' : '⛔ non autorisée'}
          </div>

          <div>
            Compte: {accountActive ? 'actif' : 'inactif'}
            {suspendedAt ? (
              <> • suspendu depuis le {fmtDate(suspendedAt)}</>
            ) : null}
          </div>

          {suspensionReason ? (
            <div>Motif de suspension: {suspensionReason}</div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}
